/**
 * Jobs list — DataTable enterprise + filtros por status + create.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Briefcase, Download, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/page-header";
import { JobStatusPill, UrgencyPill } from "@/components/status-pill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { bulkApi } from "@/lib/api-bulk";
import { downloadCsv } from "@/lib/api-system";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { clientsApi } from "@/lib/api-cadastros";
import { jobsApi, type Job, type JobStatus, type JobUrgency } from "@/lib/api-jobs";
import { formatCurrency, formatDateTime, formatRelative } from "@/lib/format";

const STATUS_FILTERS: { value: JobStatus | "all"; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "open", label: "Abertos" },
  { value: "dispatched", label: "Distribuídos" },
  { value: "assigned", label: "Atribuídos" },
  { value: "in_progress", label: "Em atendimento" },
  { value: "completed", label: "Concluídos" },
  { value: "approved", label: "Aprovados" },
  { value: "cancelled", label: "Cancelados" },
];

export function JobsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState<JobStatus | "all">("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Partial<Job>>({ urgency: "normal", price: 0 });
  const [bulkConfirm, setBulkConfirm] = useState<{ ids: string[] } | null>(null);

  const bulkDelete = useMutation({
    mutationFn: (ids: string[]) => bulkApi.deleteJobs(ids),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      toast.success(`${result.deleted} chamado(s) excluído(s)`);
      if (result.failed.length > 0) {
        toast.warning(`${result.failed.length} falha(s)`);
      }
      setBulkConfirm(null);
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const list = useQuery({
    queryKey: ["jobs", status],
    queryFn: () => jobsApi.list(status === "all" ? { size: 200 } : { size: 200, status_filter: status }),
  });
  const clients = useQuery({ queryKey: ["clients-mini"], queryFn: () => clientsApi.list({ size: 200 }) });

  const create = useMutation({
    mutationFn: (body: Partial<Job>) => jobsApi.create(body),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      toast.success("Chamado criado", { description: created.code });
      setOpen(false);
      setForm({ urgency: "normal", price: 0 });
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const columns = useMemo<ColumnDef<Job>[]>(() => [
    {
      id: "code",
      accessorKey: "code",
      header: "Código",
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">{row.original.code}</span>
      ),
    },
    {
      id: "title",
      accessorKey: "title",
      header: "Título",
      cell: ({ row }) => (
        <div className="min-w-0 max-w-md">
          <p className="font-medium truncate">{row.original.title}</p>
          <p className="text-xs text-muted-foreground">{formatRelative(row.original.created_at)}</p>
        </div>
      ),
    },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <JobStatusPill status={row.original.status} />,
    },
    {
      id: "urgency",
      accessorKey: "urgency",
      header: "Urgência",
      cell: ({ row }) => <UrgencyPill urgency={row.original.urgency} />,
    },
    {
      id: "price",
      accessorKey: "price",
      header: "Valor",
      cell: ({ row }) => (
        <span className="font-semibold tabular-nums">{formatCurrency(row.original.price)}</span>
      ),
    },
    {
      id: "scheduled_at",
      accessorKey: "scheduled_at",
      header: "Agendado",
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{formatDateTime(row.original.scheduled_at)}</span>
      ),
    },
    {
      id: "assigned_nerd_id",
      header: "Atribuído",
      cell: ({ row }) =>
        row.original.assigned_nerd_id ? (
          <Badge variant="primary">Sim</Badge>
        ) : (
          <Badge variant="muted">Não</Badge>
        ),
    },
  ], []);

  return (
    <>
      <PageHeader
        title="Chamados"
        description="Atendimentos técnicos — abertos, em andamento e histórico."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => downloadCsv("jobs")}>
              <Download className="h-4 w-4" /> Exportar
            </Button>
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" /> Novo chamado
            </Button>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        searchPlaceholder="Buscar por código, título..."
        emptyTitle="Nenhum chamado registrado"
        emptyDescription="Crie o primeiro chamado pra começar a distribuir trabalho."
        emptyAction={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> Novo chamado
          </Button>
        }
        onRowClick={(j) => navigate(`/jobs/${j.id}`)}
        enableSelection
        getRowId={(j) => j.id}
        bulkActions={(rows) => (
          <Button
            size="sm"
            variant="outline"
            className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
            onClick={() => setBulkConfirm({ ids: rows.map((r) => r.id) })}
          >
            <Trash2 className="h-3.5 w-3.5" /> Excluir selecionados
          </Button>
        )}
        toolbarRight={
          <Select value={status} onValueChange={(v) => setStatus(v as JobStatus | "all")}>
            <SelectTrigger className="w-44 h-9">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        columnLabels={{
          code: "Código",
          title: "Título",
          status: "Status",
          urgency: "Urgência",
          price: "Valor",
          scheduled_at: "Agendado",
          assigned_nerd_id: "Atribuído",
        }}
      />

      <ConfirmDialog
        open={!!bulkConfirm}
        onOpenChange={(v) => !v && setBulkConfirm(null)}
        variant="danger"
        title={`Excluir ${bulkConfirm?.ids.length ?? 0} chamado(s)?`}
        description="Esta ação não pode ser desfeita. Comentários, anexos e dispatches relacionados serão removidos."
        confirmLabel="Sim, excluir todos"
        loading={bulkDelete.isPending}
        onConfirm={() => bulkConfirm && bulkDelete.mutate(bulkConfirm.ids)}
      />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent size="lg">
          <SheetHeader>
            <SheetTitle>Novo chamado</SheetTitle>
            <SheetDescription>
              <Briefcase className="h-4 w-4 inline -mt-0.5 mr-1" />
              Atendimento técnico para um cliente.
            </SheetDescription>
          </SheetHeader>
          <SheetBody className="space-y-4">
            <Field label="Cliente" required>
              <Select
                value={form.client_id ?? ""}
                onValueChange={(v) => setForm({ ...form, client_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.data?.items.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.trade_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Título" required>
              <Input
                value={form.title ?? ""}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ex: Notebook não liga após queda"
              />
            </Field>
            <Field label="Descrição">
              <Textarea
                rows={3}
                value={form.description ?? ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Urgência">
                <Select
                  value={form.urgency ?? "normal"}
                  onValueChange={(v) => setForm({ ...form, urgency: v as JobUrgency })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="same_day">Mesmo dia</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Valor estimado (R$)">
                <Input
                  type="number"
                  step="0.01"
                  value={form.price ?? 0}
                  onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) })}
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Solicitante">
                <Input
                  value={form.requester_name ?? ""}
                  onChange={(e) => setForm({ ...form, requester_name: e.target.value })}
                />
              </Field>
              <Field label="Telefone solicitante">
                <Input
                  value={form.requester_phone ?? ""}
                  onChange={(e) => setForm({ ...form, requester_phone: e.target.value })}
                />
              </Field>
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="outline">Cancelar</Button>
            </SheetClose>
            <Button
              onClick={() => create.mutate(form)}
              disabled={create.isPending || !form.client_id || !form.title}
            >
              {create.isPending ? "Criando..." : "Criar chamado"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">
        {label} {required && <span className="text-rose-600">*</span>}
      </Label>
      {children}
    </div>
  );
}
